import {
  ChangeEvent,
  ClipboardEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import AddPhotoIcon from '@material-ui/icons/AddPhotoAlternate';
import CloseIcon from '@material-ui/icons/Close';
import {
  alertApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_TYPES,
  FeedbackPriority,
  FeedbackResult,
  FeedbackType,
  SCREENSHOT_CONTENT_TYPES,
  SCREENSHOT_MAX_BYTES,
  SCREENSHOT_MAX_FILES,
  feedbackApiRef,
} from '../api';
import {
  PRIORITY_HELP,
  PRIORITY_LABELS,
  TYPE_HELP,
  TYPE_LABELS,
} from './labels';

const FEATURE_MIN = 2;
const FEATURE_MAX = 200;
const FEEDBACK_MIN = 10;
const FEEDBACK_MAX = 5000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SCREENSHOT_MAX_MB = Math.round(SCREENSHOT_MAX_BYTES / 1024 / 1024);
const ACCEPT = SCREENSHOT_CONTENT_TYPES.join(',');

const useStyles = makeStyles(theme => ({
  thumbs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  thumb: {
    position: 'relative',
    width: 96,
    height: 72,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    background: theme.palette.background.default,
  },
  thumbImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  remove: {
    position: 'absolute',
    top: 2,
    right: 2,
    padding: 2,
    background: theme.palette.background.paper,
    '&:hover': { background: theme.palette.background.paper },
  },
  hiddenInput: {
    display: 'none',
  },
}));

export interface FeedbackFormProps {
  /** Called after the work item was created. */
  onSubmitted?: (result: FeedbackResult) => void;
  /** Page the developer was on; sent along, not shown as a field. */
  pageUrl?: string;
  /** When given, a Cancel button is shown and calls this. */
  onCancel?: () => void;
}

interface FormState {
  email: string;
  /** Empty until the developer picks one: the team triages by it. */
  type: FeedbackType | '';
  priority: FeedbackPriority;
  feature: string;
  feedback: string;
}

/** One attached screenshot with its preview. */
interface Attached {
  id: string;
  file: File;
  previewUrl?: string;
}

const emptyState = (email = ''): FormState => ({
  email,
  type: '',
  priority: 'nice_to_have',
  feature: '',
  feedback: '',
});

function validate(state: FormState): Partial<Record<keyof FormState, string>> {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!EMAIL_PATTERN.test(state.email.trim())) {
    errors.email = 'Enter a valid email address';
  }
  if (!state.type) {
    errors.type = 'Choose the kind of feedback';
  }
  const feature = state.feature.trim();
  if (feature.length < FEATURE_MIN) {
    errors.feature = 'Name the feature or page this is about';
  } else if (feature.length > FEATURE_MAX) {
    errors.feature = `At most ${FEATURE_MAX} characters`;
  }
  const feedback = state.feedback.trim();
  if (feedback.length < FEEDBACK_MIN) {
    errors.feedback = `At least ${FEEDBACK_MIN} characters — tell us what happened and what you expected`;
  } else if (feedback.length > FEEDBACK_MAX) {
    errors.feedback = `At most ${FEEDBACK_MAX} characters`;
  }
  return errors;
}

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

function previewUrlFor(file: File): string | undefined {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : undefined;
}

function revokePreview(attached: Attached) {
  if (attached.previewUrl && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(attached.previewUrl);
  }
}

/**
 * Checks candidate files against the backend's limits. Returns what can be
 * added and one sentence per reason something was left out. Exported for
 * tests.
 */
export function acceptScreenshots(
  candidates: File[],
  alreadyAttached: number,
): { accepted: File[]; rejected: string[] } {
  const accepted: File[] = [];
  const rejected: string[] = [];
  let room = SCREENSHOT_MAX_FILES - alreadyAttached;
  for (const file of candidates) {
    if (!(SCREENSHOT_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      rejected.push(
        `${file.name || 'That file'} is not a PNG, JPEG, WebP or GIF image`,
      );
      continue;
    }
    if (file.size > SCREENSHOT_MAX_BYTES) {
      rejected.push(
        `${file.name || 'That image'} is over ${SCREENSHOT_MAX_MB} MB`,
      );
      continue;
    }
    if (room <= 0) {
      rejected.push(
        `At most ${SCREENSHOT_MAX_FILES} screenshots per submission`,
      );
      break;
    }
    accepted.push(file);
    room -= 1;
  }
  return { accepted, rejected: Array.from(new Set(rejected)) };
}

/** Pasted images all arrive as "image.png": give each its own name. */
function namePasted(file: File, index: number): File {
  const ext = EXTENSION_BY_TYPE[file.type] ?? 'png';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return new File([file], `pasted-${stamp}-${index + 1}.${ext}`, {
    type: file.type,
  });
}

export const FeedbackForm = ({
  onSubmitted,
  pageUrl,
  onCancel,
}: FeedbackFormProps) => {
  const classes = useStyles();
  const feedbackApi = useApi(feedbackApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);

  const [state, setState] = useState<FormState>(() => emptyState());
  const [screenshots, setScreenshots] = useState<Attached[]>([]);
  const [screenshotError, setScreenshotError] = useState<string>();
  const [profileName, setProfileName] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenshotsRef = useRef<Attached[]>([]);
  screenshotsRef.current = screenshots;

  // Pre-fill the email from the signed-in account; the developer can still
  // change it (e.g. to a team mailbox).
  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(profile => {
        if (cancelled) return;
        setProfileName(profile.displayName);
        if (profile.email) {
          setState(prev =>
            prev.email ? prev : { ...prev, email: profile.email! },
          );
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  // Preview URLs are browser memory; free them when the form goes away.
  useEffect(() => () => screenshotsRef.current.forEach(revokePreview), []);

  const errors = validate(state);
  const hasErrors = Object.keys(errors).length > 0;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState(prev => ({ ...prev, [key]: value }));

  const addScreenshots = useCallback((candidates: File[]) => {
    setScreenshots(prev => {
      const { accepted, rejected } = acceptScreenshots(candidates, prev.length);
      setScreenshotError(rejected.length ? rejected.join('. ') : undefined);
      if (!accepted.length) return prev;
      return [
        ...prev,
        ...accepted.map(file => ({
          id: `${file.name}-${file.size}-${Math.random()
            .toString(36)
            .slice(2)}`,
          file,
          previewUrl: previewUrlFor(file),
        })),
      ];
    });
  }, []);

  const removeScreenshot = (id: string) =>
    setScreenshots(prev => {
      const gone = prev.find(s => s.id === id);
      if (gone) revokePreview(gone);
      return prev.filter(s => s.id !== id);
    });

  const clearScreenshots = () => {
    screenshots.forEach(revokePreview);
    setScreenshots([]);
    setScreenshotError(undefined);
  };

  const onFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    addScreenshots(Array.from(event.target.files ?? []));
    // Let the same file be picked again after removing it.
    event.target.value = '';
  };

  // Ctrl+V anywhere in the form with an image on the clipboard attaches it.
  const onPaste = (event: ClipboardEvent<HTMLFormElement>) => {
    const files = Array.from(event.clipboardData?.files ?? []).filter(f =>
      f.type.startsWith('image/'),
    );
    if (!files.length) return;
    event.preventDefault();
    addScreenshots(files.map(namePasted));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (hasErrors || !state.type) return;

    setSubmitting(true);
    try {
      const result = await feedbackApi.submit({
        email: state.email.trim(),
        type: state.type,
        priority: state.priority,
        feature: state.feature.trim(),
        feedback: state.feedback.trim(),
        pageUrl: pageUrl || undefined,
        authorName: profileName,
        screenshots: screenshots.map(s => s.file),
      });
      const { workItem, teamsDelivered } = result;
      alertApi.post({
        message: `Thanks! Created ${workItem.type} #${
          workItem.id
        } on the platform backlog${
          teamsDelivered ? ' and posted it to Teams' : ''
        }.`,
        severity: 'success',
        display: 'transient',
      });
      setState(emptyState(state.email.trim()));
      clearScreenshots();
      setTouched(false);
      onSubmitted?.(result);
    } catch (error) {
      // Nothing is stored server-side, so keep what was typed for a retry.
      alertApi.post({
        message: `Feedback was not sent: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} onPaste={onPaste} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            required
            value={state.email}
            onChange={e => update('email', e.target.value)}
            error={touched && !!errors.email}
            helperText={
              (touched && errors.email) || 'Your email (team account)'
            }
            inputProps={{ 'data-testid': 'feedback-email' }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            variant="outlined"
            fullWidth
            required
            error={touched && !!errors.type}
          >
            <InputLabel id="feedback-type-label">
              Type of the feedback
            </InputLabel>
            <Select
              labelId="feedback-type-label"
              label="Type of the feedback"
              value={state.type}
              onChange={e => update('type', e.target.value as FeedbackType)}
              inputProps={{ 'data-testid': 'feedback-type' }}
              SelectDisplayProps={
                { 'data-testid': 'feedback-type-select' } as Record<
                  string,
                  unknown
                >
              }
            >
              {FEEDBACK_TYPES.map(t => (
                <MenuItem key={t} value={t}>
                  {TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {(touched && errors.type) ||
                (state.type
                  ? TYPE_HELP[state.type]
                  : 'Becomes a task for the platform team to triage')}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="feedback-priority-label">Priority</InputLabel>
            <Select
              labelId="feedback-priority-label"
              label="Priority"
              value={state.priority}
              onChange={e =>
                update('priority', e.target.value as FeedbackPriority)
              }
              inputProps={{ 'data-testid': 'feedback-priority' }}
            >
              {FEEDBACK_PRIORITIES.map(p => (
                <MenuItem key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{PRIORITY_HELP[state.priority]}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Feature"
            variant="outlined"
            fullWidth
            required
            placeholder="e.g. Modify Application, Create wizard, Cost estimate"
            value={state.feature}
            onChange={e => update('feature', e.target.value)}
            error={touched && !!errors.feature}
            helperText={
              (touched && errors.feature) ||
              'The feature you want to discuss; becomes the work item title'
            }
            inputProps={{
              maxLength: FEATURE_MAX,
              'data-testid': 'feedback-feature',
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Feedback"
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={6}
            value={state.feedback}
            onChange={e => update('feedback', e.target.value)}
            error={touched && !!errors.feedback}
            helperText={
              (touched && errors.feedback) ||
              `${
                state.feedback.trim().length
              }/${FEEDBACK_MAX} — what happened, what you expected, and how to reproduce it if relevant`
            }
            inputProps={{
              maxLength: FEEDBACK_MAX,
              'data-testid': 'feedback-message',
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className={classes.hiddenInput}
              onChange={onFilesPicked}
              data-testid="feedback-screenshot-input"
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddPhotoIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={
                submitting || screenshots.length >= SCREENSHOT_MAX_FILES
              }
              data-testid="feedback-add-screenshot"
            >
              Add screenshots
            </Button>
            <Typography variant="body2" color="textSecondary">
              {screenshots.length
                ? `${screenshots.length}/${SCREENSHOT_MAX_FILES} attached`
                : `Optional — or paste an image (Ctrl+V) anywhere in this form. Up to ${SCREENSHOT_MAX_FILES}, ${SCREENSHOT_MAX_MB} MB each.`}
            </Typography>
          </Box>
          {screenshotError ? (
            <FormHelperText error data-testid="feedback-screenshot-error">
              {screenshotError}
            </FormHelperText>
          ) : null}
          {screenshots.length ? (
            <div className={classes.thumbs} data-testid="feedback-screenshots">
              {screenshots.map(s => (
                <Tooltip key={s.id} title={s.file.name}>
                  <div
                    className={classes.thumb}
                    data-testid="feedback-screenshot"
                  >
                    {s.previewUrl ? (
                      <img
                        src={s.previewUrl}
                        alt={s.file.name}
                        className={classes.thumbImage}
                      />
                    ) : null}
                    <IconButton
                      size="small"
                      className={classes.remove}
                      onClick={() => removeScreenshot(s.id)}
                      aria-label={`Remove ${s.file.name}`}
                      disabled={submitting}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : null}
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gridGap={8}>
            {onCancel ? (
              <Button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                data-testid="feedback-cancel"
              >
                Cancel
              </Button>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={submitting || (touched && hasErrors)}
              data-testid="feedback-submit"
            >
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};
